import { Controller } from "@hotwired/stimulus";

// In-app Savant copilot panel. Holds the conversation client-side and posts the
// running history to /ask_savant; renders the assistant's reply. The server
// scopes reads/actions to the signed-in user's Savant permissions and never
// sends anything external.
export default class extends Controller {
  static targets = ["panel", "messages", "input"];

  connect() {
    // Rehydrate conversation from sessionStorage so the copilot panel
    // survives Turbo navigations.  Keyed per signed-in user (not path) so
    // history follows the user across pages without leaking to whoever
    // else uses the browser/session, and capped to avoid unbounded growth.
    const userId = document.querySelector('meta[name="current-user-id"]')?.content;
    this.storageKey = userId ? `ask_savant_history:${userId}` : null;
    this.history = [];
    this.busy = false;

    if (!this.storageKey) return;

    try {
      const saved = sessionStorage.getItem(this.storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          this.history = parsed.slice(-50);
          this.#rehydrateMessages();
        }
      }
    } catch (_e) {
      // sessionStorage might be unavailable (private mode, quota); degrade
      // gracefully to a fresh conversation.
    }
  }

  open(event) {
    event?.preventDefault();
    this.panelTarget.classList.remove("hidden");
    requestAnimationFrame(() => {
      this.panelTarget.classList.add("ask-open");
      this.inputTarget?.focus();
    });
  }

  close(event) {
    event?.preventDefault();
    this.panelTarget.classList.remove("ask-open");
    setTimeout(() => this.panelTarget.classList.add("hidden"), 200);
  }

  toggle(event) {
    event?.preventDefault();
    if (this.panelTarget.classList.contains("hidden")) this.open();
    else this.close();
  }

  key(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      this.send();
    }
  }

  submit(event) {
    event?.preventDefault();
    this.send();
  }

  async send() {
    const text = this.inputTarget.value.trim();
    if (!text || this.busy) return;

    this.inputTarget.value = "";
    this.appendMessage("user", text);
    this.history.push({ role: "user", content: text });
    this.#persistHistory();
    this.setBusy(true);

    try {
      const res = await fetch("/ask_savant", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken(),
          Accept: "application/json",
        },
        body: JSON.stringify({ messages: this.history, context: this.pageContext() }),
      });
      const data = await res.json().catch(() => ({}));
      const answer = (data && data.text) || data.error || "Savant could not answer that.";
      this.appendMessage("assistant", answer);
      this.history.push({ role: "assistant", content: answer });
      this.#persistHistory();
    } catch (_e) {
      this.appendMessage("assistant", "Savant could not respond just now. Please try again.");
    } finally {
      this.setBusy(false);
    }
  }

  appendMessage(role, text) {
    const row = document.createElement("div");
    row.className = role === "user" ? "flex justify-end" : "flex justify-start";
    const bubble = document.createElement("div");
    bubble.className =
      role === "user"
        ? "max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-sm border border-accent-500/30 bg-accent-500/15 px-3 py-2 text-fg"
        : "max-w-[92%] whitespace-pre-wrap rounded-2xl rounded-bl-sm border border-line bg-ink-800 px-3 py-2 text-fg";
    bubble.textContent = text;
    row.appendChild(bubble);
    this.messagesTarget.appendChild(row);
    this.scrollDown();
  }

  setBusy(busy) {
    this.busy = busy;
    if (busy) {
      const row = document.createElement("div");
      row.dataset.thinking = "1";
      row.className = "flex justify-start";
      row.innerHTML =
        '<div class="rounded-2xl rounded-bl-sm border border-line bg-ink-800 px-3 py-2 text-fg-muted">Reading your data…</div>';
      this.messagesTarget.appendChild(row);
      this.scrollDown();
    } else {
      this.messagesTarget.querySelector("[data-thinking]")?.remove();
    }
  }

  scrollDown() {
    this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight;
  }

  #persistHistory() {
    if (!this.storageKey) return;

    try {
      sessionStorage.setItem(this.storageKey, JSON.stringify(this.history.slice(-50)));
    } catch (_e) {
      // Quota exceeded or unavailable; silently skip persistence.
    }
  }

  #rehydrateMessages() {
    this.history.forEach(({ role, content }) => {
      if (role === "user" || role === "assistant") {
        this.appendMessage(role, content);
      }
    });
    this.scrollDown();
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || "";
  }

  pageContext() {
    const main = document.querySelector("main");
    const heading = main?.querySelector("h1")?.innerText || document.querySelector("h1")?.innerText || "";
    const breadcrumb = document.querySelector("[data-shell-breadcrumb]")?.innerText || "";
    const mainText = (main?.innerText || "").replace(/\s+\n/g, "\n").trim();

    return {
      path: window.location.pathname,
      title: document.title,
      breadcrumb: breadcrumb.slice(0, 160),
      heading: heading.slice(0, 240),
      main_text: mainText.slice(0, 5000),
    };
  }
}
